// Snap-to system — snaps layer positions, selections, and guide creations
// to document bounds, grid lines, other layers, and guides.
//
// A snap is a "magnetic" pull: if a dragged position comes within
// `SNAP_THRESHOLD` doc-space pixels of a snap target, the position
// is clamped to that target value. Callers on a zoomed viewport should
// derive a doc-space threshold from a fixed *screen*-space pixel budget
// (threshold = screenPx / zoom) so the pull feels the same size regardless
// of zoom — see snapPoint's wiring onto ToolContext in main.ts (audit i26).

export const SNAP_THRESHOLD = 5; // default px in document space

export interface SnapTargets {
  /** Document bounds (left, right, top, bottom edges) */
  docBounds: { left: number; right: number; top: number; bottom: number };
  /** Grid lines at regular intervals */
  gridLines: { vertical: number[]; horizontal: number[] };
  /** User-defined guide positions */
  guides: { vertical: number[]; horizontal: number[] };
  /** Center of the document */
  docCenter: { x: number; y: number };
  /** Golden ratio / rule-of-thirds points */
  ruleOfThirds: { vertical: number[]; horizontal: number[] };
  /** Edges + centers (left/right/center-x, top/bottom/center-y) of other layers */
  layerEdges: { vertical: number[]; horizontal: number[] };
}

export interface SnapSettings {
  enabled: boolean;
  snapToGrid: boolean;
  snapToGuides: boolean;
  snapToDocBounds: boolean;
  snapToLayers: boolean;
  snapToCenter: boolean;
  snapToThirds: boolean;
}

export function defaultSnapSettings(): SnapSettings {
  return {
    enabled: true,
    snapToGrid: true,
    snapToGuides: true,
    snapToDocBounds: true,
    snapToLayers: false,
    snapToCenter: false,
    snapToThirds: false,
  };
}

export interface LayerRect {
  readonly x: number;
  readonly y: number;
  readonly w: number;
  readonly h: number;
}

/**
 * Build vertical/horizontal snap candidates (left/right/center-x,
 * top/bottom/center-y) from a set of layer rects. Callers are responsible
 * for excluding the layer(s) currently being dragged from `rects` — snapping
 * a layer to its own (possibly stale) bounds is a no-op at best and a subtle
 * "sticky drag" bug at worst.
 */
export function buildLayerEdgeTargets(rects: readonly LayerRect[]): { vertical: number[]; horizontal: number[] } {
  const vertical: number[] = [];
  const horizontal: number[] = [];
  for (const r of rects) {
    vertical.push(r.x, r.x + r.w, r.x + r.w / 2);
    horizontal.push(r.y, r.y + r.h, r.y + r.h / 2);
  }
  return { vertical, horizontal };
}

/**
 * Build snap targets for a document.
 */
export function buildSnapTargets(
  docW: number,
  docH: number,
  gridSize: number,
  guides: { vertical: number[]; horizontal: number[] },
  layerEdges: { vertical: number[]; horizontal: number[] } = { vertical: [], horizontal: [] },
): SnapTargets {
  // Grid lines
  const verticalGrid: number[] = [];
  const horizontalGrid: number[] = [];
  if (gridSize > 0) {
    for (let x = 0; x <= docW; x += gridSize) verticalGrid.push(x);
    for (let y = 0; y <= docH; y += gridSize) horizontalGrid.push(y);
  }

  // Rule of thirds
  const thirdV = [docW / 3, docW * 2 / 3];
  const thirdH = [docH / 3, docH * 2 / 3];

  return {
    docBounds: { left: 0, right: docW, top: 0, bottom: docH },
    gridLines: { vertical: verticalGrid, horizontal: horizontalGrid },
    guides,
    docCenter: { x: docW / 2, y: docH / 2 },
    ruleOfThirds: { vertical: thirdV, horizontal: thirdH },
    layerEdges,
  };
}

/**
 * Snap a single coordinate value against a list of target values. Returns
 * the CLOSEST target within `threshold`, or null if none qualify.
 */
function snapCoord(value: number, targets: number[], threshold: number): number | null {
  let best: number | null = null;
  let bestDist = Infinity;
  for (const t of targets) {
    const dist = Math.abs(value - t);
    if (dist <= threshold && dist < bestDist) {
      best = t;
      bestDist = dist;
    }
  }
  return best;
}

function xCandidates(targets: SnapTargets, settings: SnapSettings): number[] {
  const out: number[] = [];
  if (settings.snapToDocBounds) out.push(targets.docBounds.left, targets.docBounds.right);
  if (settings.snapToCenter) out.push(targets.docCenter.x);
  if (settings.snapToGrid) out.push(...targets.gridLines.vertical);
  if (settings.snapToGuides) out.push(...targets.guides.vertical);
  if (settings.snapToThirds) out.push(...targets.ruleOfThirds.vertical);
  if (settings.snapToLayers) out.push(...targets.layerEdges.vertical);
  return out;
}

function yCandidates(targets: SnapTargets, settings: SnapSettings): number[] {
  const out: number[] = [];
  if (settings.snapToDocBounds) out.push(targets.docBounds.top, targets.docBounds.bottom);
  if (settings.snapToCenter) out.push(targets.docCenter.y);
  if (settings.snapToGrid) out.push(...targets.gridLines.horizontal);
  if (settings.snapToGuides) out.push(...targets.guides.horizontal);
  if (settings.snapToThirds) out.push(...targets.ruleOfThirds.horizontal);
  if (settings.snapToLayers) out.push(...targets.layerEdges.horizontal);
  return out;
}

/**
 * Snap a point/position to the nearest snap target.
 * Returns the snapped position (or original if no snap target is close enough).
 *
 * This is the ONE consolidated snap function for the app (audit i26) — it
 * replaces the previously-duplicated, dead `snapCoord`/`snapToLayersOrBounds`
 * helpers that used to live in main.ts.
 */
export function snapPosition(
  x: number,
  y: number,
  targets: SnapTargets,
  settings: SnapSettings,
  threshold: number = SNAP_THRESHOLD,
): { x: number; y: number; snappedX: boolean; snappedY: boolean } {
  if (!settings.enabled) return { x, y, snappedX: false, snappedY: false };

  let snappedX = false;
  let snappedY = false;

  const snappedXVal = snapCoord(x, xCandidates(targets, settings), threshold);
  if (snappedXVal !== null) { x = snappedXVal; snappedX = true; }

  const snappedYVal = snapCoord(y, yCandidates(targets, settings), threshold);
  if (snappedYVal !== null) { y = snappedYVal; snappedY = true; }

  return { x, y, snappedX, snappedY };
}

/**
 * Check if a layer edge (or position) is close enough to snap.
 * Returns the closest snapped position or null.
 */
export function snapLayerEdge(
  dragPos: number,
  layerEdges: number[],
  threshold: number = SNAP_THRESHOLD,
): number | null {
  return snapCoord(dragPos, layerEdges, threshold);
}
